(() => {
  const internalTypePattern = /\s+tag:"cat:type-(?:tire|assembly|wheel|part)"/gi;
  const hasInternalTypePattern = /(?:^|\s)tag:"cat:type-(?:tire|assembly|wheel|part)"(?:\s|$)/i;
  const standardTireSizePattern = /\b(?:P|LT|ST)?\s*\d{3}\s*\/\s*\d{2}\s*R\s*\d{2}\b/i;
  const compactTireSizePattern = /\b(?:P|LT|ST)?\d{7}\b/i;
  const assemblyIntentPattern = /\b(?:assembly|assemblies|bundle|tire\s*(?:&|and)\s*wheel)\b/i;
  const wheelIntentPattern = /\b(?:wheel|wheels|rim|rims)\b/i;

  const cleanSearchTerms = (terms) => String(terms || '')
    .replace(internalTypePattern, '')
    .replace(/\s+/g, ' ')
    .trim();

  // Spaced/hyphenated sizes ('205 75 15', '205-75-15') normalize to slash
  // form first so the standard pattern and routing can catch them. Same
  // plausibility gate as the compact rewrite.
  const normalizeSpacedTireSize = (terms) => terms.replace(
    /\b(P|LT|ST)?\s*(\d{3})[\s-]+(\d{2})[\s-]+R?\s*(\d{2})\b/i,
    (match, prefix = '', width, ratio, rim) => {
      const w = Number(width);
      const a = Number(ratio);
      const r = Number(rim);
      const plausible = w >= 105 && w <= 445 && w % 5 === 0 && a >= 25 && a <= 90 && r >= 8 && r <= 30;
      return plausible ? `${prefix}${width}/${ratio}R${rim}` : match;
    }
  );

  const normalizeCompactTireSize = (terms) => terms.replace(
    /\b(P|LT|ST)?(\d{3})(\d{2})(\d{2})\b/i,
    (match, prefix = '', width, ratio, rim) => {
      // Only rewrite digits that form a plausible tire size. Anything else
      // (part numbers, SKUs, phone fragments) must pass through untouched.
      const w = Number(width);
      const a = Number(ratio);
      const r = Number(rim);
      const plausible = w >= 105 && w <= 445 && w % 5 === 0 && a >= 25 && a <= 90 && r >= 8 && r <= 30;
      return plausible ? `${prefix}${width}/${ratio}R${rim}` : match;
    }
  );

  const withPrimaryType = (terms, type) => `${cleanSearchTerms(terms)} tag:"cat:type-${type}"`;

  const routeSearchTerms = (terms) => {
    const originalTerms = String(terms || '').trim();
    if (!originalTerms || hasInternalTypePattern.test(originalTerms)) return originalTerms;

    let routedTerms = cleanSearchTerms(originalTerms);
    if (!standardTireSizePattern.test(routedTerms) && compactTireSizePattern.test(routedTerms)) {
      routedTerms = normalizeCompactTireSize(routedTerms);
    }
    if (!standardTireSizePattern.test(routedTerms)) {
      routedTerms = normalizeSpacedTireSize(routedTerms);
    }

    if (!standardTireSizePattern.test(routedTerms)) return routedTerms;
    if (assemblyIntentPattern.test(routedTerms)) return withPrimaryType(routedTerms, 'assembly');
    if (wheelIntentPattern.test(routedTerms)) return withPrimaryType(routedTerms, 'wheel');
    return withPrimaryType(routedTerms, 'tire');
  };

  window.C2GSearchRouting = Object.freeze({
    clean: cleanSearchTerms,
    route: routeSearchTerms,
    withPrimaryType
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = new URL(form.action, window.location.href);
    if (!action.pathname.endsWith('/search')) return;
    if (form.querySelector('select')?.value?.trim()) return;

    const input = form.querySelector('input[name="q"]');
    if (input) input.value = routeSearchTerms(input.value);
  }, true);

  if (window.location.pathname.endsWith('/search')) {
    const searchUrl = new URL(window.location.href);
    const searchTerms = searchUrl.searchParams.get('q');
    const explicitType = searchUrl.searchParams.get('c2g_type');
    const requestedType = searchUrl.searchParams.get('type');
    // Routing only makes sense for product results: leave article/page
    // searches alone instead of forcing them to type=product.
    const isProductSearch = !requestedType || requestedType.split(',').includes('product');
    const alreadyRouted = hasInternalTypePattern.test(searchTerms || '');

    if (searchTerms && explicitType !== 'all' && isProductSearch && !alreadyRouted) {
      const routedTerms = routeSearchTerms(searchTerms);
      // Redirect only when routing actually changed the query (type tag added
      // or compact size expanded) — a whitespace-only difference would loop.
      if (routedTerms !== cleanSearchTerms(searchTerms)) {
        searchUrl.searchParams.set('q', routedTerms);
        searchUrl.searchParams.set('type', 'product');
        window.location.replace(searchUrl.toString());
        return;
      }
    }

    document.title = cleanSearchTerms(document.title);
  }

  const money = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);

  const setMoneyText = (element, value) => {
    if (!element) return;
    const textNode = Array.from(element.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    const currencyCode = textNode?.textContent.trim().match(/\s([A-Z]{3})$/)?.[1];
    const formattedValue = currencyCode ? `${value} ${currencyCode}` : value;
    if (textNode) {
      textNode.textContent = `${formattedValue}\n`;
    } else {
      element.prepend(document.createTextNode(formattedValue));
    }
  };

  const updateProductTotal = (input, nextUnitPrice, nextCompareUnitPrice) => {
    const form = input.closest('.product-form');
    const total = form?.querySelector('[data-c2g-pdp-total]');
    const productInfo = input.closest('.product-info');
    const headline = productInfo?.querySelector('[data-c2g-headline-price]');
    if (!total && !headline) return;

    const quantity = Math.max(1, Number.parseInt(input.value, 10) || 1);
    const unitPrice = Number.parseInt(nextUnitPrice ?? total?.dataset.unitPrice ?? headline?.dataset.unitPrice, 10) || 0;
    const compareUnitPrice = Number.parseInt(nextCompareUnitPrice ?? headline?.dataset.compareUnitPrice, 10) || 0;
    const orderTotal = money(quantity * unitPrice);

    if (total) {
      total.dataset.unitPrice = String(unitPrice);
      total.textContent = orderTotal;
    }

    if (headline) {
      headline.dataset.unitPrice = String(unitPrice);
      headline.dataset.compareUnitPrice = String(compareUnitPrice);
      const priceRoot = headline.querySelector('.price');
      const regularGroup = headline.querySelector('.price-regular');
      const saleGroup = headline.querySelector('.price-sale');
      const salePrice = headline.querySelector('.price-item-sale.price-item-last');
      const regularPrice = headline.querySelector('.price-regular .price-item-regular');
      const comparePrice = headline.querySelector('.price-discount .price-item-regular');

      const saleIsActive = compareUnitPrice > unitPrice && Boolean(saleGroup && salePrice);
      priceRoot?.classList.toggle('price-on-sale', saleIsActive);

      if (saleIsActive) {
        regularGroup?.setAttribute('hidden', '');
        saleGroup.removeAttribute('hidden');
        setMoneyText(salePrice, orderTotal);
        setMoneyText(comparePrice, money(quantity * compareUnitPrice));
      } else {
        regularGroup?.removeAttribute('hidden');
        saleGroup?.setAttribute('hidden', '');
        setMoneyText(regularPrice || salePrice, orderTotal);
      }
    }
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.productVariantChange, ({ sectionId, variant }) => {
      const price = document.getElementById(`price-${sectionId}`);
      const productInfo = price?.closest('.product-info');
      const input = productInfo?.querySelector('form[data-type="add-to-cart-form"] input[name="quantity"]');
      if (input && variant) {
        updateProductTotal(input, variant.price, variant.compare_at_price || 0);
      }
    });
  }

  document.addEventListener('input', (event) => {
    if (event.target.matches('.template-product .quantity-input-field')) {
      updateProductTotal(event.target);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('.template-product .quantity-input-field')) {
      updateProductTotal(event.target);
    }
  });

  document.addEventListener('click', (event) => {
	const viewBtn = event.target.closest('[data-c2g-view]');
	if (viewBtn) {
	  const isList = viewBtn.dataset.c2gView === 'list';
	  document.documentElement.classList.toggle('c2g-view-list', isList);
	  try { localStorage.setItem('c2g-view', isList ? 'list' : 'grid'); } catch (e) { /* private mode */ }
	  return;
	}

	const filterToggle = event.target.closest('[data-c2g-sidebar-toggle]');
	if (filterToggle) {
	  const layout = filterToggle.closest('.facets-wrap');
	  if (!layout) return;

	  const isCollapsed = layout.classList.toggle('c2g-filters-collapsed');
	  const label = filterToggle.querySelector('[data-c2g-sidebar-toggle-label]');
	  filterToggle.setAttribute('aria-expanded', String(!isCollapsed));
	  filterToggle.setAttribute('aria-label', isCollapsed ? 'Show filters' : 'Hide filters');
	  if (label) label.textContent = isCollapsed ? 'Show Filters' : 'Hide Filters';
	  return;
	}

    const button = event.target.closest('.template-product .quantity-input-button');
    if (!button) return;
    requestAnimationFrame(() => {
      const input = button.closest('quantity-input')?.querySelector('.quantity-input-field');
      if (input) updateProductTotal(input);
    });
  });

  // Nested drawer accordion: categories expand inline (native <details>) and
  // each group inside starts collapsed, toggled by its heading. The headings
  // are markup shared with the desktop mega menu, so button semantics are
  // added here at runtime, only inside the drawer.
  const drawerNav = document.querySelector('.c2g-mobile-nav');
  if (drawerNav) {
    drawerNav.querySelectorAll('.c2g-mobile-nav__panel .c2g-nav-group h3').forEach((heading) => {
      heading.setAttribute('role', 'button');
      heading.setAttribute('tabindex', '0');
      heading.setAttribute('aria-expanded', 'false');
    });

    const toggleDrawerGroup = (heading) => {
      const group = heading.closest('.c2g-nav-group');
      const open = group.classList.toggle('is-open');
      heading.setAttribute('aria-expanded', String(open));
    };

    drawerNav.addEventListener('click', (event) => {
      const heading = event.target.closest('.c2g-mobile-nav__panel .c2g-nav-group h3');
      if (heading) toggleDrawerGroup(heading);
    });

    drawerNav.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const heading = event.target.closest('.c2g-mobile-nav__panel .c2g-nav-group h3');
      if (!heading) return;
      event.preventDefault();
      toggleDrawerGroup(heading);
    });
  }
})();

